/* Shared biometric ingestion pipeline (mirrors api/internal/biometric/ingest.go):
 * normalize → reject → dedupe → resolve employee via mappings → store → device stats. */
import { dedupeKey, parseCsvPunches, parseGenericEvent, type ParsedLine } from "../../domain/biometric";
import type * as T from "../types";
import { pad, type Ctx } from "./ctx";
import type { DeviceRec } from "./state";

export interface ParseResult { lines: ParsedLine[]; rejected: string[] }
export const MAX_ERRORS = 50;

export function nextPunchId(c: Ctx): string {
  const n = c.nextId("punches", 1, () => c.state.punches.reduce((m, p) => Math.max(m, Number(/(\d+)$/.exec(p.id)?.[1] ?? 0)), 0));
  return "p" + pad(n, 6);
}

/** Device-specific mapping first, then "*". */
export function resolveEmployee(c: Ctx, deviceId: string, user: string): number | null {
  const ms = c.state.mappings;
  const exact = ms.find((m) => m.deviceId === deviceId && m.deviceUserId === user);
  if (exact) return exact.employeeId;
  const any = ms.find((m) => m.deviceId === "*" && m.deviceUserId === user);
  return any ? any.employeeId : null;
}

/** UTC offset (minutes) of an IANA zone at the clock's "now"; 180 on error. */
export function tzOffsetMinutes(tz: string, nowIso: string): number {
  if (!tz) return 180;
  try {
    const at = new Date(nowIso + "Z");
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }).formatToParts(at);
    const g = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
    const local = Date.UTC(g("year"), g("month") - 1, g("day"), g("hour") % 24, g("minute"), g("second"));
    return Math.round((local - at.getTime()) / 60000);
  } catch {
    return 180;
  }
}

export function validTimezone(tz: string): boolean {
  if (!tz || tz === "Local") return false;
  try { new Intl.DateTimeFormat("en-US", { timeZone: tz }); return true; } catch { return false; }
}

export function ingest(c: Ctx, dev: DeviceRec, res: ParseResult, source: T.Punch["source"]): T.IngestResult {
  const out: T.IngestResult = { accepted: 0, duplicates: 0, unmatched: 0, rejected: 0, errors: [] };
  if (!dev.claimed || !dev.tenantId) return out;
  const addErr = (m: string) => { if (out.errors.length < MAX_ERRORS) out.errors.push(m.length > 200 ? m.slice(0, 200) : m); };
  for (const r of res.rejected) { out.rejected++; addErr("rejected: " + r); }
  let lastPunch = "";
  for (const l of res.lines) {
    const dir = dev.directionMode && dev.directionMode !== "device" ? "unknown" : l.direction;
    const key = dedupeKey(dev.id, l.deviceUserId, l.time, dir);
    if (c.state.dedupe.has(key)) { out.duplicates++; continue; }
    const p: T.Punch = {
      id: nextPunchId(c), deviceId: dev.id, deviceUserId: l.deviceUserId, employeeId: resolveEmployee(c, dev.id, l.deviceUserId),
      time: l.time, direction: dir, verify: l.verify, source, dedupeKey: key, receivedAt: c.now, raw: l.raw,
    };
    c.state.punches.push(p);
    c.state.dedupe.add(key);
    out.accepted++;
    if (p.employeeId == null) out.unmatched++;
    if (p.time > lastPunch) lastPunch = p.time;
  }
  touchDevice(c, dev, out.accepted, lastPunch);
  return out;
}

function touchDevice(c: Ctx, dev: DeviceRec, accepted: number, lastPunch: string) {
  dev.punchCount += accepted;
  if (lastPunch && (!dev.lastPunchAt || lastPunch > dev.lastPunchAt)) dev.lastPunchAt = lastPunch;
  if (accepted > 0) dev.userCount = new Set(c.state.punches.filter((p) => p.deviceId === dev.id).map((p) => p.deviceUserId)).size;
  dev.lastSeen = c.now;
  if (dev.status !== "pending") dev.status = "online";
}

/** Flatten webhook payload shapes: object, array, {events:[…]}, Hikvision AccessControllerEvent / AcsEvent.InfoList. */
export function extractEvents(top: unknown): Record<string, unknown>[] {
  const out: Record<string, unknown>[] = [];
  const isObj = (v: unknown): v is Record<string, unknown> => v != null && typeof v === "object" && !Array.isArray(v);
  const walk = (v: unknown) => {
    if (Array.isArray(v)) { v.forEach(walk); return; }
    if (!isObj(v)) return;
    if (Array.isArray(v.events)) { walk(v.events); return; }
    if (isObj(v.AccessControllerEvent)) {
      const merged: Record<string, unknown> = {};
      for (const [k, val] of Object.entries(v)) if (k !== "AccessControllerEvent" && !isObj(val)) merged[k] = val;
      Object.assign(merged, v.AccessControllerEvent);
      out.push(merged);
      return;
    }
    if (isObj(v.AcsEvent) && Array.isArray(v.AcsEvent.InfoList)) { walk(v.AcsEvent.InfoList); return; }
    out.push(v);
  };
  walk(top);
  return out;
}

/** Parse a webhook body: JSON value, JSON text, or CSV text. */
export function parseWebhook(payload: unknown, tz: number): ParseResult {
  if (typeof payload === "string") {
    const t = payload.trim();
    if (t[0] !== "{" && t[0] !== "[") return parseCsvPunches(payload);
    payload = JSON.parse(t);
  }
  const res: ParseResult = { lines: [], rejected: [] };
  for (const o of extractEvents(payload)) {
    const l = parseGenericEvent(o, tz);
    if (l) res.lines.push(l); else res.rejected.push(JSON.stringify(o));
  }
  return res;
}
